// src/components/comments/CommentItem.tsx
import React from 'react';
import { type RecipeComment } from '../../api/Types';
import { useAuth } from '../../context/AuthContext';
import { useCommentItemActions } from '../../domains/comments';
import { Alert, Avatar, Box, Button, Flex, IconButton, ListGroupItem, Text, Textarea } from '../ui';

interface CommentItemProps {
  comment: RecipeComment;
  onCommentUpdated: (updatedComment: RecipeComment) => void;
  onCommentDeleted: (commentId: number) => void;
}

const CommentItem: React.FC<CommentItemProps> = ({ comment, onCommentUpdated, onCommentDeleted }) => {
  const { author, token } = useAuth();
  const isAuthor = author?.id === comment.author_id;
  const {
    isEditing,
    editedComment,
    submitting,
    error,
    setEditedComment,
    startEditing,
    cancelEditing,
    handleUpdate,
    handleDelete,
  } = useCommentItemActions({
    comment,
    isAuthor,
    token,
    onCommentUpdated,
    onCommentDeleted,
  });

  return (
    <Flex as={ListGroupItem} direction="column" align="start" borderless>
      {isEditing ? (
        <Box fullWidth>
          <Textarea
            marginBottom={2}
            value={editedComment}
            onChange={(e) => setEditedComment(e.target.value)}
          />
          <Flex justify="end">
            <Button
              size="sm"
              variant="secondary"
              marginEnd={2}
              onClick={cancelEditing}
              disabled={submitting}
            >
              Annuler
            </Button>
            <Button size="sm" onClick={handleUpdate} disabled={submitting}>
              {submitting ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </Flex>
        </Box>
      ) : (
        <Flex fullWidth justify={isAuthor ? 'end' : 'start'} marginBottom={2}>
          <Flex
            direction="column"
            marginStart={isAuthor ? 'auto' : undefined}
            marginEnd={isAuthor ? undefined : 'auto'}
            paddingBottom={isAuthor ? 2 : undefined}
            className="comment-bubble"
            style={{
              backgroundColor: isAuthor ? '#e0f7fa' : '#f0f0f0', // Neutral background, slightly different for author
              border: `2px solid ${comment.author?.color || 'transparent'}`, // Author color border
              borderRadius: '0.5rem', // Rounded corners for the bubble
              padding: '0.75rem', // Padding inside the bubble
              maxWidth: '75%', // Limit bubble width
              wordBreak: 'break-word', // Ensure long words break
            } as React.CSSProperties}
          >
            <Flex fullWidth justify="between" marginBottom={1}>
              <Flex align="center">
                {comment.author?.avatar_url && (
                  <Avatar
                    src={comment.author.avatar_url}
                    alt={`Avatar de ${comment.author.first_name}`}
                    marginEnd={2}
                    size={24}
                    style={{
                      border: `2px solid ${comment.author.color || 'transparent'}` /* Author color border */
                    }}
                  />
                )}
                <Text as="span" marginBottom={0} className="comment-author-name" style={{ color: 'black' }}> {/* Forced to black */}
                  {comment.author?.first_name} {comment.author?.name}
                </Text>
              </Flex>
              <Text as="small" className="comment-timestamp">{new Date(comment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Text>
            </Flex>
            <Text marginBottom={1} marginEnd={2}>{comment.comment}</Text>
            {isAuthor && (
              <Flex className="comment-actions" marginTop={2}>
                <IconButton
                  label="Modifier"
                  variant="link"
                  size="sm"
                  radius={false}
                  padding={0}
                  textDecoration="none"
                  onClick={startEditing}
                  title="Modifier"
                >
                  ✏️
                </IconButton>
                <IconButton
                  label="Supprimer"
                  variant="link"
                  size="sm"
                  radius={false}
                  padding={0}
                  marginStart={1}
                  textColor="danger"
                  textDecoration="none"
                  onClick={handleDelete}
                  disabled={submitting}
                  title="Supprimer"
                >
                  {submitting ? '...' : '🗑️'}
                </IconButton>
              </Flex>
            )}
          </Flex>
        </Flex>
      )}
      {error && <Alert variant="danger" marginTop={2}>{error}</Alert>}
    </Flex>
  );
};

export default CommentItem;
